/**
 * Contourist configurator.
 *
 * Visual grammar, layout, state shape and interaction model ported from the
 * Claude Design canvas (Contourist.dc.html / Poster.dc.html / Shirt.dc.html):
 * stepped wizard on desktop, preview-first sheets on phone, duo/rainbow/
 * custom-gradient art styles, real garment colors, poster + tee as separate
 * purchasable formats.
 *
 * What's real instead of the design prototype's placeholders:
 *  - Contour lines come from contourist-topo-data.json (real SRTM-traced
 *    paths), not the prototype's seeded fake ridge generator. The "density"
 *    slider selects an evenly-elevation-spaced subset of the real traced
 *    lines rather than re-tracing at a different interval (that needs a
 *    server-side retrace, out of scope this round).
 *  - Two real Shopify products (poster, tee) back the two purchasable
 *    formats; add-to-cart hits the real Shopify cart with the creative
 *    fields as line-item properties. No Printful API this round.
 */
(function () {
  "use strict";

  // Loaded on two pages: the configurator, which owns #cx-app, and the home
  // page's Contourist feature section, which reuses this file's artwork
  // renderer through window.CxPreview (see the bottom) instead of drawing
  // its own previews. Everything above the boot block is shared; only the
  // configurator's own boot is gated on #cx-app being present.
  var root = document.getElementById('cx-app');

  var TOPO_URL = root && root.getAttribute('data-topo-url');
  var MAPTILER_KEY = (root && root.getAttribute('data-maptiler-key')) || '';
  var TILE_URL_TEMPLATE = 'https://api.maptiler.com/tiles/terrain-rgb-v2/{z}/{x}/{y}.webp?key=' + MAPTILER_KEY;
  var ADMIN = new URLSearchParams(window.location.search).get('admin') === '1';

  var posterDataEl = document.getElementById('cx-poster-product-data');
  var teeDataEl = document.getElementById('cx-tee-product-data');
  var POSTER_PRODUCT = posterDataEl ? JSON.parse(posterDataEl.textContent) : null;
  var TEE_PRODUCT = teeDataEl ? JSON.parse(teeDataEl.textContent) : null;

  /* ── reference data, ported from the design canvas ──────────────────── */
  // halfHKm matches data-pipeline/trace_from_tiles.py's default extent per
  // location -- also the coverage-radius slider's starting position.
  var LOC_META = {
    popo_izta:    { sku: 'POP', region: 'Puebla / State of México, MX', short: 'MEXICO', halfHKm: 20 },
    triglav:      { sku: 'TRG', region: 'Julian Alps, Slovenia', short: 'SLOVENIA', halfHKm: 14 },
    mont_blanc:   { sku: 'MTB', region: 'Graian Alps, France / Italy', short: 'FRANCE / ITALY', halfHKm: 16 },
    kilimanjaro:  { sku: 'KIL', region: 'Kilimanjaro Region, Tanzania', short: 'TANZANIA', halfHKm: 18 },
    machu_picchu: { sku: 'MPC', region: 'Cusco Region, Peru', short: 'PERU', halfHKm: 14 },
    fuji:         { sku: 'FUJ', region: 'Shizuoka / Yamanashi, Japan', short: 'JAPAN', halfHKm: 16 }
  };
  var RADIUS_MIN_KM = 6, RADIUS_MAX_KM = 40, RADIUS_DEFAULT_KM = 16;

  var STYLES = [
    { id: 'duo', name: 'Gold duotone', after: 'After the Popocatépetl tee', note: 'Warm gold to maroon on near black, thin gold rule, text kept quiet.', sku: 'GD' },
    { id: 'rainbow', name: 'Full spectrum', after: 'After the Triglav tee', note: 'Rainbow contours inside a thick rounded frame, title up top, coordinates set large.', sku: 'FS' },
    { id: 'custom', name: 'Your gradient', after: 'New, you set both ends', note: 'Two colours, low ground to summit, and a slider for how many lines get traced.', sku: 'CU' }
  ];
  var GRAD_FROM = ['#e0160a', '#8a1538', '#2b3548', '#1a7f6b', '#6b3fa0', '#252525'];
  var GRAD_TO = ['#edb85a', '#f4f2ef', '#7ad1c4', '#f2a0c8', '#a8d84a', '#9c4a2a'];

  // Real Ñom Studio garment stock, sampled from the supplier photography.
  // Same four hexes double as poster paper stock (Charcoal/Ivory/Slate/Sand).
  var GARMENTS = [
    { hex: '#444444', name: 'Faded Black' },
    { hex: '#e7e3da', name: 'Faded Bone' },
    { hex: '#546070', name: 'Faded Indigo' },
    { hex: '#cab698', name: 'Faded Khaki' }
  ];
  var PAPER_NAMES = { '#444444': 'Charcoal', '#e7e3da': 'Ivory', '#546070': 'Slate', '#cab698': 'Sand' };
  var SIZES = ['S', 'M', 'L', 'XL', '2XL'];
  var POSTER_SIZES = [
    { id: 'A4', dim: '21 × 29,7 cm' },
    { id: 'A3', dim: '29,7 × 42 cm' },
    { id: 'A2', dim: '42 × 59,4 cm' },
    { id: 'A1', dim: '59,4 × 84,1 cm' }
  ];
  var TABS = [['loc', 'Location'], ['style', 'Style'], ['words', 'Words'], ['order', 'Order']];
  var STEP_NAMES = ['Location', 'Style', 'Words', 'Size & order'];

  var TEE_SILHOUETTE = "M232,44 C232,22 268,6 320,6 C372,6 408,22 408,44 L470,66 C512,82 552,120 578,172 L528,246 L470,208 L470,706 C470,728 448,744 416,744 L224,744 C192,744 170,728 170,706 L170,208 L112,246 L62,172 C88,120 128,82 170,66 Z";

  var DATA = null; // real topo data (6 featured, seeded; plus 'custom' once a search is traced)
  var state = {
    loc: null, style: 'duo',
    from: '#e0160a', to: '#edb85a', density: 22,
    title: 'STILL PEAKING.', msg: '', edition: '', date: '',
    garment: '#444444', size: 'L', posterSize: 'A3', view: 'poster',
    step: 1, tab: 'loc',
    query: '',
    radiusKm: RADIUS_DEFAULT_KM,       // coverage radius for the current location
    searchResults: [],                 // Nominatim results for the current query
    searching: false,                  // debounced geocode request in flight
    tracing: false,                    // live retrace (search select or radius drag) in flight
    tracingReason: null,               // 'search' | 'radius', for status-message placement
    traceError: null,                  // message shown if a live retrace fails
    customCenter: null                 // { lat, lon } once a search result is picked
  };

  /* ── color / math helpers, ported from Poster.dc.html ──────────────────── */
  function lerp(a, b, t) { return a + (b - a) * t; }
  function hexToRgb(h) { h = h.replace('#', ''); return [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)]; }
  function toHex(c) { return '#' + c.map(function (v) { return Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0'); }).join(''); }
  function mixA(a, b, t) { return [lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]; }
  function lumOf(c) { return (0.299 * c[0] + 0.587 * c[1] + 0.114 * c[2]) / 255; }
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }

  function deriveTheme(style, paperHex, fromHex, toHex_) {
    var pr = hexToRgb(paperHex);
    var light = lumOf(pr) > 0.55;
    var duoA = light ? [74, 26, 20] : [140, 31, 44];
    var duoB = light ? [168, 104, 24] : [237, 184, 90];
    var ramp = [duoA, duoB];
    var theme = style === 'rainbow'
      ? (light
        ? { ink: '#252525', body: '#3a3a3a', meta: '#3a3a3a', frame: '#252525', rule: '#252525' }
        : { ink: '#f4f2ef', body: '#e6e2da', meta: '#c9c3b8', frame: '#f4f2ef', rule: '#f4f2ef' })
      : (light
        ? { ink: '#3a1b12', body: '#4a3a30', meta: '#8a5a33', frame: '#7a4a22', rule: 'rgba(58,27,18,.35)' }
        : { ink: '#edb85a', body: '#e4d4bf', meta: '#9d6a45', frame: '#c9922f', rule: '#4a3220' });

    if (style === 'custom') {
      var cA = hexToRgb(fromHex || '#e0160a'), cB = hexToRgb(toHex_ || '#edb85a');
      var hi = lumOf(cA) >= lumOf(cB) ? cA : cB;
      var lo = hi === cA ? cB : cA;
      ramp = light ? [mixA(lo, [0, 0, 0], 0.12), mixA(hi, lo, 0.3)] : [lo, hi];
      var ink = light ? lo : hi;
      theme = {
        ink: toHex(ink), frame: toHex(mixA(ink, pr, 0.12)),
        body: toHex(mixA(ink, pr, 0.3)), meta: toHex(mixA(ink, pr, 0.5)),
        rule: toHex(mixA(ink, pr, 0.68))
      };
    }
    return { ramp: ramp, theme: theme, light: light };
  }

  /* ── contour paths: real data, density-subsampled, straight-stitched ──
     Matches the design's toPath(): M/L segments, points closer than 0.35
     (in a 0-100 space) are dropped, no smoothing added on top of the trace.
  */
  function pathD(points) {
    var d = '', px = null, py = null;
    for (var i = 0; i < points.length; i++) {
      var x = points[i][0] * 100, y = (1 - points[i][1]) * 100;
      var last = i === points.length - 1;
      if (!last && px !== null && Math.abs(x - px) < 0.35 && Math.abs(y - py) < 0.35) continue;
      d += (i === 0 ? 'M' : 'L') + x.toFixed(2) + ' ' + y.toFixed(2);
      px = x; py = y;
    }
    return d;
  }

  // Stable priority order over indices 0..n-1 via binary subdivision: the
  // two extremes first, then the midpoint, then the quarter-points, then
  // the eighth-points, and so on. Taking the first K of this order is
  // always an evenly-spread subset, and K+1 always adds exactly one index
  // on top of K -- unlike recomputing evenly-spaced indices from scratch
  // per count, which reshuffles the whole selection as count changes.
  function priorityOrder(n) {
    if (n <= 0) return [];
    var order = [0], seen = { 0: true };
    if (n > 1) { order.push(n - 1); seen[n - 1] = true; }
    var queue = [[0, n - 1]];
    while (queue.length && order.length < n) {
      var next = [];
      for (var i = 0; i < queue.length; i++) {
        var lo = queue[i][0], hi = queue[i][1];
        if (hi - lo > 1) {
          var mid = Math.floor((lo + hi) / 2);
          if (!seen[mid]) { seen[mid] = true; order.push(mid); }
          next.push([lo, mid], [mid, hi]);
        }
      }
      queue = next;
    }
    return order;
  }

  // Selects `count` real traced lines out of the full set, spread by
  // elevation fraction via the stable priority order above, so "density"
  // thins/thickens the real trace -- and nudging the slider only adds or
  // removes lines at the margin, it never swaps out ones already shown.
  function subsample(paths, count) {
    var n = paths.length;
    if (count >= n) return paths;
    var sorted = paths.slice().sort(function (a, b) { return a.e - b.e; });
    var idxs = priorityOrder(n).slice(0, count).sort(function (a, b) { return a - b; });
    return idxs.map(function (idx) { return sorted[idx]; });
  }

  function contourLines(locId, density, thumb, style, themeInfo) {
    var loc = DATA[locId];
    var count = thumb ? Math.max(6, Math.round(density * 0.6)) : density;
    var picked = subsample(loc.paths, Math.min(count, loc.paths.length));
    // Stroke weight scales inversely with density, on top of the existing
    // elevation-based weighting, so "Coarse" reads as genuinely bolder/fewer
    // and "Fine" as genuinely thinner/more numerous, not just a line-count
    // change that gets lost among similarly-weighted strokes.
    var widthScale = thumb ? 1 : lerp(1.6, 0.7, (Math.min(50, Math.max(20, density)) - 20) / 30);
    return picked.map(function (seg) {
      var d = pathD(seg.p);
      if (thumb) {
        return { d: d, c: seg.e > 0.72 ? '#e0160a' : '#252525', w: seg.e > 0.72 ? 3.4 : 2.6 };
      }
      var col, w;
      if (style === 'rainbow') {
        var h = 252 - 252 * seg.e;
        col = 'hsl(' + h.toFixed(0) + ' ' + (themeInfo.light ? 74 : 82) + '% ' + (themeInfo.light ? (seg.e > 0.8 ? 46 : 44) : (seg.e > 0.8 ? 62 : 58)) + '%)';
        w = lerp(0.34, 0.62, seg.e) * widthScale;
      } else {
        var ramp = themeInfo.ramp;
        col = toHex([lerp(ramp[0][0], ramp[1][0], seg.e), lerp(ramp[0][1], ramp[1][1], seg.e), lerp(ramp[0][2], ramp[1][2], seg.e)]);
        w = lerp(0.3, 0.58, seg.e) * widthScale;
      }
      return { d: d, c: col, w: w };
    });
  }

  function svgLines(lines) {
    return lines.map(function (l) {
      return '<path d="' + l.d + '" fill="none" stroke="' + l.c + '" stroke-width="' + l.w + '" stroke-linejoin="round"></path>';
    }).join('');
  }

  /* ── poster/tee artwork fragments (shared by preview + admin export) ── */
  function posterFragment(locId, style, opts) {
    opts = opts || {};
    var loc = DATA[locId];
    var meta = LOC_META[locId] || { short: '' };
    var thumb = opts.mode === 'thumb';
    var paperHex = opts.bare ? (opts.paperForTheme || '#171012') : (opts.paper || (style === 'rainbow' ? '#f0ede8' : '#171012'));
    var themeInfo = deriveTheme(style, paperHex, opts.from, opts.to);
    var dens = Math.max(20, Math.min(50, Math.round(opts.density || 22)));
    var lines = contourLines(locId, dens, thumb, style, themeInfo);
    var title = esc(opts.title || loc.title);
    var message = esc(opts.message || '');
    var edition = esc(opts.edition || '');
    var date = esc(opts.date || '');
    var coords = fmtCoords(loc.center, true);
    var bg = opts.bare ? 'transparent' : paperHex;

    if (thumb) {
      return '<div style="width:100%;aspect-ratio:1/1;overflow:hidden;display:flex;align-items:center;justify-content:center">' +
        '<svg viewBox="4 4 92 92" preserveAspectRatio="xMidYMid slice" style="width:100%;height:100%;display:block">' + svgLines(lines) + '</svg></div>';
    }

    var t = themeInfo.theme;
    if (style !== 'rainbow') {
      return '<div style="width:100%;aspect-ratio:3/4;background:' + bg + ';padding:4cqw;box-sizing:border-box">' +
        '<div style="height:100%;border:0.3cqw solid ' + t.frame + ';padding:5cqw 4.6cqw 4cqw;box-sizing:border-box;display:flex;flex-direction:column;gap:2.6cqw">' +
        '<div style="font-family:\'Bebas Neue\',Impact,sans-serif;color:' + t.ink + ';font-size:8.6cqw;line-height:.88;letter-spacing:-.005em;text-transform:uppercase;text-wrap:balance">' + title + '</div>' +
        '<div style="flex:1;min-height:0;display:flex;align-items:center;justify-content:center"><svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;display:block">' + svgLines(lines) + '</svg></div>' +
        '<div style="font-family:\'Inter\',sans-serif;font-weight:300;color:' + t.body + ';font-size:2.9cqw;line-height:1.55">' + message + '</div>' +
        '<div style="display:flex;justify-content:space-between;gap:2cqw;font-family:\'JetBrains Mono\',monospace;font-size:1.9cqw;letter-spacing:.16em;text-transform:uppercase;color:' + t.meta + '"><span>' + edition + '</span><span>' + date + '</span><span>' + meta.short + '</span></div>' +
        '<div style="font-family:\'JetBrains Mono\',monospace;color:' + t.ink + ';font-size:3.1cqw;letter-spacing:.1em;text-align:center;border-top:0.15cqw solid ' + t.rule + ';padding-top:2.4cqw">' + coords + '</div>' +
        '</div></div>';
    }
    return '<div style="width:100%;aspect-ratio:3/4;background:' + bg + ';padding:5cqw 5cqw 4cqw;box-sizing:border-box;display:flex;flex-direction:column;gap:3cqw">' +
      '<div style="font-family:\'Bebas Neue\',Impact,sans-serif;color:' + t.ink + ';font-size:10.5cqw;line-height:.84;letter-spacing:-.015em;text-transform:uppercase;text-wrap:balance">' + title + '</div>' +
      '<div style="flex:1;min-height:0;border:1.5cqw solid ' + t.frame + ';border-radius:5cqw;overflow:hidden;display:flex;align-items:center;justify-content:center"><svg viewBox="6 6 88 88" preserveAspectRatio="xMidYMid slice" style="width:100%;height:100%;display:block">' + svgLines(lines) + '</svg></div>' +
      '<div style="font-family:\'Inter\',sans-serif;font-weight:400;color:' + t.body + ';font-size:2.9cqw;line-height:1.5">' + message + '</div>' +
      '<div style="display:flex;justify-content:space-between;gap:2cqw;font-family:\'JetBrains Mono\',monospace;font-size:1.9cqw;letter-spacing:.16em;text-transform:uppercase;color:' + t.meta + '"><span>' + edition + '</span><span>' + date + '</span><span>' + meta.short + '</span></div>' +
      '<div style="font-family:\'JetBrains Mono\',monospace;color:' + t.ink + ';font-size:4.4cqw;letter-spacing:.01em;text-align:center;line-height:1">' + coords + '</div>' +
      '</div>';
  }

  function posterCard(locId, style, opts, extraStyle) {
    return '<div style="width:100%;container-type:inline-size' + (extraStyle || '') + '">' + posterFragment(locId, style, opts) + '</div>';
  }

  // Real garment photography (back view, matching product media assigned
  // per colour variant in Shopify). Print-area position (left/top/width
  // below) is a first-pass estimate against the photo's actual back-panel
  // framing, not a confirmed Printful mockup spec. Falls back to the vector
  // silhouette if a variant has no photo assigned yet.
  function teeFragment(locId, style, garmentHex, opts, photoUrl) {
    var artOpts = Object.assign({}, opts, { bare: true, paperForTheme: garmentHex, paper: undefined });
    var garment = photoUrl
      ? '<img src="' + photoUrl + '" alt="" style="position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;filter:drop-shadow(0 10px 22px rgba(0,0,0,.18))">'
      : '<svg viewBox="0 0 640 780" style="position:absolute;inset:0;width:100%;height:100%;display:block;filter:drop-shadow(0 10px 22px rgba(0,0,0,.18))"><path d="' + TEE_SILHOUETTE + '" fill="' + garmentHex + '"></path></svg>';
    return '<div style="width:100%;aspect-ratio:1/1;position:relative;overflow:hidden">' +
      garment +
      '<div style="position:absolute;left:30%;top:19%;width:40%;container-type:inline-size">' + posterFragment(locId, style, artOpts) + '</div>' +
      '</div>';
  }

  function fmtCoords(center, short) {
    var lat = center[0], lon = center[1];
    var ns = lat >= 0 ? 'N' : 'S', ew = lon >= 0 ? 'E' : 'W';
    if (short) return Math.abs(lat).toFixed(2) + '° ' + ns + ' ' + Math.abs(lon).toFixed(2) + '° ' + ew;
    return Math.abs(lat).toFixed(4) + '° ' + ns + '  /  ' + Math.abs(lon).toFixed(4) + '° ' + ew;
  }

  /* ── derived view-model, mirrors Contourist.dc.html's renderVals() ──────────────────── */
  function computeVals() {
    var s = state;
    var loc = DATA[s.loc];
    var meta = LOC_META[s.loc] || {};
    var st = STYLES.filter(function (x) { return x.id === s.style; })[0] || STYLES[0];
    var isPoster = s.view === 'poster';
    var gObj = GARMENTS.filter(function (g) { return g.hex === s.garment; })[0] || GARMENTS[0];
    var colourName = isPoster ? (PAPER_NAMES[gObj.hex] || gObj.name) : gObj.name;

    var posterVariant = resolveVariant(POSTER_PRODUCT, { Color: PAPER_NAMES[s.garment] || gObj.name, Size: s.posterSize });
    var teeVariant = resolveVariant(TEE_PRODUCT, { Color: gObj.name, Size: s.size });
    var variant = isPoster ? posterVariant : teeVariant;
    var price = variant ? variant.price / 100 : null;

    var sku = 'CTR-' + (meta.sku || '') + '-' + st.sku + '-' + (isPoster ? s.posterSize : s.size) + '-' + colourName.slice(0, 2).toUpperCase();

    return { loc: loc, meta: meta, st: st, isPoster: isPoster, gObj: gObj, colourName: colourName, posterVariant: posterVariant, teeVariant: teeVariant, variant: variant, price: price, sku: sku };
  }

  function resolveVariant(product, wanted) {
    if (!product) return null;
    if (product.has_only_default_variant) return product.variants[0];
    var positions = {};
    product.options_with_values.forEach(function (opt, idx) {
      var key = Object.keys(wanted).filter(function (k) { return k.toLowerCase() === opt.name.toLowerCase() || (/colou?r/i.test(opt.name) && k === 'Color'); })[0];
      positions[idx + 1] = key ? wanted[key] : null;
    });
    return product.variants.filter(function (v) {
      return v.options.every(function (val, i) { return positions[i + 1] == null || val === positions[i + 1]; });
    })[0] || null;
  }

  function eur(n) { return n == null ? '—' : '€' + n.toFixed(2).replace('.', ',').replace(/,00$/, ',00'); }

  /* ── render: desktop wizard ──────────────────── */
  function render() {
    if (!DATA) return;
    var v = computeVals();
    renderStage(v);
    renderColorStrip(v);
    renderCaption(v);
    renderSteps(v);
    renderPriceHead(v);
    renderPhone(v);
    syncViewToggle();
    syncProperties(v);
  }

  // The desktop Poster/Shirt toggle buttons are static markup (unlike the
  // phone toggle, which render() rebuilds from scratch each time), so their
  // pressed state needs an explicit sync after every click.
  function syncViewToggle() {
    var wrap = document.querySelector('[data-cx-view-toggle]');
    if (!wrap) return;
    var btns = wrap.querySelectorAll('button');
    if (btns[0]) btns[0].setAttribute('aria-pressed', state.view === 'poster' ? 'true' : 'false');
    if (btns[1]) btns[1].setAttribute('aria-pressed', state.view === 'shirt' ? 'true' : 'false');
  }

  function renderStage(v) {
    document.querySelectorAll('[data-cx-stage]').forEach(function (el) {
      var artOpts = { title: state.title, message: state.msg, edition: state.edition, date: state.date, density: state.density, from: state.from, to: state.to };
      var html;
      if (v.isPoster) {
        artOpts.paper = state.garment;
        html = '<div class="cx-stage-inner cx-poster">' + posterCard(state.loc, state.style, artOpts) + '</div>';
      } else {
        html = '<div class="cx-stage-inner">' + teeFragment(state.loc, state.style, state.garment, artOpts, v.teeVariant && v.teeVariant.image) + '</div>';
      }
      el.innerHTML = html;
    });
  }

  function renderColorStrip(v) {
    document.querySelectorAll('[data-cx-color-label]').forEach(function (el) { el.textContent = v.isPoster ? 'Paper stock' : 'Garment colour'; });
    document.querySelectorAll('[data-cx-color-name]').forEach(function (el) { el.textContent = v.colourName; });
    document.querySelectorAll('[data-cx-garments]').forEach(function (wrap) {
      wrap.innerHTML = '';
      GARMENTS.forEach(function (g) {
        var name = v.isPoster ? (PAPER_NAMES[g.hex] || g.name) : g.name;
        var btn = document.createElement('button');
        btn.className = 'cx-swatch';
        btn.type = 'button';
        btn.title = name;
        btn.setAttribute('aria-pressed', g.hex === state.garment ? 'true' : 'false');
        btn.innerHTML = '<span style="background:' + g.hex + '"></span>';
        btn.addEventListener('click', function () { state.garment = g.hex; render(); });
        wrap.appendChild(btn);
      });
    });
  }

  function renderCaption(v) {
    var interval = Math.round(v.loc.zmax / state.density / 10) * 10;
    document.querySelectorAll('[data-cx-caption]').forEach(function (el) {
      el.innerHTML =
        '<span>' + (state.loc === 'custom' ? 'Live-traced elevation' : 'Real elevation data') + '</span>' +
        '<span>' + Math.round(v.loc.zmax) + ' m summit</span>' +
        '<span>' + state.density + ' contours</span>' +
        '<span>' + interval + ' m interval</span>' +
        '<span>' + (v.isPoster ? 'Print ' + state.posterSize + ', ' + posterDim(state.posterSize) : 'Back print 3:4, 30 × 40 cm') + '</span>';
    });
  }

  function posterDim(id) { var p = POSTER_SIZES.filter(function (x) { return x.id === id; })[0]; return p ? p.dim : ''; }

  function renderPriceHead(v) {
    document.querySelectorAll('[data-cx-price-label]').forEach(function (el) { el.textContent = eur(v.price); });
    document.querySelectorAll('[data-cx-price-note]').forEach(function (el) { el.textContent = v.isPoster ? state.posterSize + ', made to order' : 'Made to order'; });
  }

  /* ── steps (desktop wizard) ──────────────────── */
  function renderSteps(v) {
    for (var n = 1; n <= 4; n++) {
      var open = state.step === n, done = state.step > n, reached = state.step >= n;
      document.querySelectorAll('[data-cx-step="' + n + '"]').forEach(function (el) {
        el.querySelectorAll('[data-cx-step-num]').forEach(function (e) { e.classList.toggle('cx-active', open); e.classList.toggle('cx-reached', reached && !open); });
        el.querySelectorAll('[data-cx-step-name]').forEach(function (e) { e.classList.toggle('cx-active', open); e.classList.toggle('cx-reached', reached && !open); });
        el.querySelectorAll('[data-cx-step-body]').forEach(function (e) { e.style.display = open ? '' : 'none'; });
        el.querySelectorAll('[data-cx-step-summary]').forEach(function (e) { e.style.display = done ? '' : 'none'; });
        el.querySelectorAll('[data-cx-step-edit]').forEach(function (e) { e.style.display = done ? '' : 'none'; });
      });
    }
    document.querySelectorAll('[data-cx-step-summary="1"]').forEach(function (e) { e.textContent = v.loc.label + ' / ' + fmtCoords(v.loc.center, true); });
    document.querySelectorAll('[data-cx-step-summary="2"]').forEach(function (e) { e.textContent = v.st.name; });
    document.querySelectorAll('[data-cx-step-summary="3"]').forEach(function (e) { e.textContent = '“' + state.title + '”'; });

    document.querySelectorAll('[data-cx-step-label]').forEach(function (e) { e.textContent = 'Step ' + state.step + ' of 4'; });
    document.querySelectorAll('[data-cx-back]').forEach(function (e) { e.style.display = state.step > 1 ? '' : 'none'; });
    document.querySelectorAll('[data-cx-continue]').forEach(function (e) { e.style.display = state.step < 4 ? '' : 'none'; });
    document.querySelectorAll('[data-cx-atc]').forEach(function (e) {
      e.style.display = state.step === 4 ? '' : 'none';
      e.textContent = 'Add to cart, ' + eur(v.price);
      e.disabled = !v.variant || !v.variant.available;
    });
    document.querySelectorAll('[data-cx-oos]').forEach(function (e) { e.style.display = (state.step === 4 && v.variant && !v.variant.available) ? '' : 'none'; });

    document.querySelectorAll('[data-cx-open4-only]').forEach(function (e) { e.style.display = state.step === 4 ? '' : 'none'; });
    document.querySelectorAll('[data-cx-order-color]').forEach(function (e) { e.textContent = v.isPoster ? 'Paper stock' : 'Garment colour' + ': ' + v.colourName; });

    renderLocationList('[data-cx-loc-list]');
    renderSearchResults('[data-cx-search-results]');
    renderRadiusPanel('[data-cx-radius-panel]');
    renderStyleCards('[data-cx-style-grid]');
    renderDensityPanel('[data-cx-density-panel]');
    renderCustomPanel('[data-cx-custom-panel]');
    renderWords();
    renderOrderPanel(v, '[data-cx-order-panel]');
  }

  // 'custom' is a reserved DATA key for whatever was last live-traced from
  // a search -- never part of the browsable featured-location list itself.
  function featuredIds() {
    return Object.keys(DATA).filter(function (id) { return id !== 'custom'; });
  }

  function renderLocationList(sel) {
    document.querySelectorAll(sel).forEach(function (list) {
      var q = (state.query || '').trim().toLowerCase();
      var all = featuredIds();
      var hits = q ? all.filter(function (id) {
        var loc = DATA[id], meta = LOC_META[id] || {};
        return (loc.label + ' ' + loc.country + ' ' + (meta.region || '')).toLowerCase().indexOf(q) >= 0;
      }) : all;
      list.innerHTML = '';
      if (!hits.length) {
        var none = document.createElement('div');
        none.className = 'cx-nohits';
        none.textContent = 'None of the featured traces match. Search below to trace anywhere else.';
        list.appendChild(none);
      }
      hits.forEach(function (id) {
        var loc = DATA[id];
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cx-rowb';
        btn.setAttribute('aria-pressed', id === state.loc ? 'true' : 'false');
        btn.innerHTML =
          '<div class="cx-loc-thumb">' + posterFragment(id, state.style, { mode: 'thumb' }) + '</div>' +
          '<div class="cx-loc-mid"><div class="cx-bb">' + esc(loc.label) + '</div><div class="cx-loc-region">' + esc((LOC_META[id] || {}).region || loc.country) + '</div></div>' +
          '<div class="cx-loc-right"><div class="cx-elev">' + Math.round(loc.zmax).toLocaleString('en-US') + ' m</div><div class="cx-lat">' + fmtCoords(loc.center, true) + '</div></div>';
        btn.addEventListener('click', function () {
          state.loc = id;
          state.radiusKm = (LOC_META[id] || {}).halfHKm || RADIUS_DEFAULT_KM;
          state.searchResults = []; state.traceError = null;
          render();
        });
        list.appendChild(btn);
      });
    });
    document.querySelectorAll('[data-cx-hitcount]').forEach(function (e) {
      var q = (state.query || '').trim().toLowerCase();
      var all = featuredIds();
      var n = q ? all.filter(function (id) { return (DATA[id].label + ' ' + DATA[id].country).toLowerCase().indexOf(q) >= 0; }).length : all.length;
      e.textContent = n + ' of ' + all.length;
    });
  }

  // Nominatim results for "search any mountain" -- separate from the
  // featured list above, only populated once a remote search has run.
  function renderSearchResults(sel) {
    document.querySelectorAll(sel).forEach(function (wrap) {
      wrap.innerHTML = '';
      if (state.searching) {
        var s = document.createElement('div'); s.className = 'cx-nohits'; s.textContent = 'Searching…'; wrap.appendChild(s); return;
      }
      if (state.tracing && state.customCenter && state.loc !== 'custom') {
        var t = document.createElement('div'); t.className = 'cx-nohits'; t.textContent = 'Tracing real elevation for this spot…'; wrap.appendChild(t); return;
      }
      if (state.traceError) {
        var e = document.createElement('div'); e.className = 'cx-nohits'; e.textContent = state.traceError; wrap.appendChild(e);
      }
      state.searchResults.forEach(function (r) {
        var btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'cx-rowb';
        btn.innerHTML =
          '<div class="cx-loc-mid"><div class="cx-bb">' + esc(r.label) + '</div><div class="cx-loc-region">' + esc(r.full) + '</div></div>' +
          '<div class="cx-loc-right"><div class="cx-lat">' + fmtCoords([r.lat, r.lon], true) + '</div></div>';
        btn.addEventListener('click', function () { selectSearchResult(r); });
        wrap.appendChild(btn);
      });
    });
  }

  function renderRadiusPanel(sel) {
    document.querySelectorAll(sel).forEach(function (panel) {
      panel.querySelectorAll('[data-cx-radius-label]').forEach(function (e) { e.textContent = state.radiusKm + ' km'; });
      panel.querySelectorAll('input[type="range"]').forEach(function (e) { e.value = state.radiusKm; });
      panel.querySelectorAll('[data-cx-radius-status]').forEach(function (e) { e.textContent = (state.tracing && state.tracingReason === 'radius') ? 'Retracing…' : ''; });
    });
  }

  /* ── search any mountain + live retrace ────────────────────
     Geocoding: OpenStreetMap Nominatim (free, no key, debounced).
     Elevation + tracing: assets/contourist-trace.js (CxTrace), the same
     recipe as data-pipeline/trace_from_tiles.py, run live in the browser.
     A traced result is injected into DATA under the location's own key
     (the 6 featured keys, or the reserved 'custom' key for search results)
     so every existing render function keeps working unmodified. */
  var geocodeTimer = null;
  function scheduleGeocode(query) {
    clearTimeout(geocodeTimer);
    if (!query || query.trim().length < 3) { state.searchResults = []; render(); return; }
    geocodeTimer = setTimeout(function () { geocode(query); }, 450);
  }

  function geocode(query) {
    state.searching = true; render();
    var url = 'https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&q=' + encodeURIComponent(query);
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('search failed'); return r.json(); })
      .then(function (results) {
        state.searching = false;
        state.searchResults = results.map(function (r) {
          return { label: (r.display_name || '').split(',')[0], full: r.display_name, lat: parseFloat(r.lat), lon: parseFloat(r.lon) };
        });
        render();
      })
      .catch(function () { state.searching = false; state.searchResults = []; render(); });
  }

  function selectSearchResult(r) {
    state.searchResults = [];
    state.customCenter = { lat: r.lat, lon: r.lon };
    state.radiusKm = RADIUS_DEFAULT_KM;
    retrace('custom', r.lat, r.lon, RADIUS_DEFAULT_KM, 'search', { label: r.label, country: r.full, title: r.label, tagline: '' });
  }

  // reason: 'search' (brand-new location, state.loc only switches on
  // success so the stage never renders an undefined DATA entry) or
  // 'radius' (adjusting the currently-shown location's coverage --
  // key === state.loc already, so the stage just keeps showing the last
  // good trace until this one lands).
  function retrace(key, lat, lon, halfHKm, reason, metaForNew) {
    state.tracing = true; state.tracingReason = reason; state.traceError = null; render();
    return CxTrace.traceLocation({ lat: lat, lon: lon, halfHKm: halfHKm, tileUrlTemplate: TILE_URL_TEMPLATE })
      .then(function (result) {
        if (!(result.zmax - result.zmin >= 30)) throw new Error('No real elevation variation found there — try a different spot.');
        var existing = DATA[key] || {};
        DATA[key] = Object.assign({}, existing, metaForNew || {}, {
          key: key, center: [lat, lon], bbox: result.bbox, zmin: result.zmin, zmax: result.zmax, paths: result.paths
        });
        state.loc = key;
        state.tracing = false; state.tracingReason = null;
        render();
      })
      .catch(function (err) {
        state.tracing = false; state.tracingReason = null;
        state.traceError = (err && err.message) || 'Could not trace that location.';
        render();
      });
  }

  var retraceTimer = null;
  function scheduleRetrace(key, lat, lon, halfHKm) {
    clearTimeout(retraceTimer);
    retraceTimer = setTimeout(function () { retrace(key, lat, lon, halfHKm, 'radius'); }, 500);
  }

  function renderStyleCards(sel) {
    document.querySelectorAll(sel).forEach(function (grid) {
      grid.innerHTML = '';
      STYLES.forEach(function (st) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'cx-style-card';
        card.setAttribute('aria-pressed', st.id === state.style ? 'true' : 'false');
        var artOpts = { title: state.title, message: state.msg, edition: state.edition, date: state.date, density: state.density, from: state.from, to: state.to, paper: state.garment };
        card.innerHTML =
          '<div class="cx-style-preview">' + posterCard(state.loc, st.id, artOpts) + '</div>' +
          '<div class="cx-style-body"><div class="cx-bb">' + st.name + '</div><div class="cx-mi">' + st.after + '</div><div class="cx-style-note">' + st.note + '</div></div>';
        card.addEventListener('click', function () { state.style = st.id; render(); });
        grid.appendChild(card);
      });
    });
  }

  // Density applies to every style, not just custom, so it renders
  // unconditionally in its own panel rather than inside the custom-only
  // gradient picker.
  function renderDensityPanel(sel) {
    document.querySelectorAll(sel).forEach(function (panel) {
      panel.querySelectorAll('[data-cx-density-label]').forEach(function (e) { e.textContent = state.density + ' lines'; });
      panel.querySelectorAll('input[type="range"]').forEach(function (e) { e.value = state.density; });
    });
  }

  function renderCustomPanel(sel) {
    document.querySelectorAll(sel).forEach(function (panel) {
      panel.style.display = state.style === 'custom' ? 'flex' : 'none';
      panel.querySelectorAll('[data-cx-from-hex]').forEach(function (e) { e.textContent = state.from; });
      panel.querySelectorAll('[data-cx-to-hex]').forEach(function (e) { e.textContent = state.to; });
      panel.querySelectorAll('[data-cx-gradient-chip]').forEach(function (e) { e.style.background = 'linear-gradient(90deg,' + state.from + ',' + state.to + ')'; });

      panel.querySelectorAll('[data-cx-from-swatches]').forEach(function (wrap) {
        wrap.innerHTML = '';
        GRAD_FROM.forEach(function (hex) {
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'cx-swatch cx-swatch-sm'; btn.title = hex;
          btn.setAttribute('aria-pressed', hex === state.from ? 'true' : 'false');
          btn.innerHTML = '<span style="background:' + hex + '"></span>';
          btn.addEventListener('click', function () { state.from = hex; render(); });
          wrap.appendChild(btn);
        });
      });
      panel.querySelectorAll('[data-cx-to-swatches]').forEach(function (wrap) {
        wrap.innerHTML = '';
        GRAD_TO.forEach(function (hex) {
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'cx-swatch cx-swatch-sm'; btn.title = hex;
          btn.setAttribute('aria-pressed', hex === state.to ? 'true' : 'false');
          btn.innerHTML = '<span style="background:' + hex + '"></span>';
          btn.addEventListener('click', function () { state.to = hex; render(); });
          wrap.appendChild(btn);
        });
      });
    });
  }

  function renderWords() {
    document.querySelectorAll('[data-cx-title-count]').forEach(function (e) { e.textContent = state.title.length + ' / 32'; });
    document.querySelectorAll('[data-cx-msg-count]').forEach(function (e) { e.textContent = state.msg.length + ' / 55'; });
    document.querySelectorAll('[data-cx-coords]').forEach(function (e) { e.textContent = fmtCoords(DATA[state.loc].center, false); });
  }

  function renderOrderPanel(v, sel) {
    document.querySelectorAll(sel).forEach(function (panel) {
      var posterBlock = panel.querySelector('[data-cx-poster-block]');
      var teeBlock = panel.querySelector('[data-cx-tee-block]');
      if (posterBlock) posterBlock.style.display = v.isPoster ? '' : 'none';
      if (teeBlock) teeBlock.style.display = v.isPoster ? 'none' : '';

      panel.querySelectorAll('[data-cx-poster-size-name]').forEach(function (e) { e.textContent = state.posterSize; });
      panel.querySelectorAll('[data-cx-poster-size-dim]').forEach(function (e) { e.textContent = posterDim(state.posterSize); });
      panel.querySelectorAll('[data-cx-poster-size-grid]').forEach(function (grid) {
        grid.innerHTML = '';
        POSTER_SIZES.forEach(function (p) {
          var variant = resolveVariant(POSTER_PRODUCT, { Color: PAPER_NAMES[state.garment] || state.garment, Size: p.id });
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'cx-poster-size-opt';
          btn.setAttribute('aria-pressed', p.id === state.posterSize ? 'true' : 'false');
          btn.innerHTML = '<span class="cx-bb">' + p.id + '</span><span class="cx-dim">' + p.dim + '</span><span class="cx-price-sm">' + eur(variant ? variant.price / 100 : null) + '</span>';
          btn.addEventListener('click', function () { state.posterSize = p.id; render(); });
          grid.appendChild(btn);
        });
      });
      panel.querySelectorAll('[data-cx-size-name]').forEach(function (e) { e.textContent = state.size; });
      panel.querySelectorAll('[data-cx-size-pills]').forEach(function (row) {
        row.innerHTML = '';
        SIZES.forEach(function (z) {
          var btn = document.createElement('button');
          btn.type = 'button'; btn.className = 'cx-pill';
          btn.setAttribute('aria-pressed', z === state.size ? 'true' : 'false');
          btn.textContent = z;
          btn.addEventListener('click', function () { state.size = z; render(); });
          row.appendChild(btn);
        });
      });
    });
  }

  /* ── phone canvas ──────────────────── */
  function renderPhone(v) {
    document.querySelectorAll('[data-cx-phone-view-toggle]').forEach(function (wrap) {
      wrap.innerHTML = '';
      [['poster', 'Poster'], ['shirt', 'On shirt']].forEach(function (pair) {
        var btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'cx-tab';
        btn.setAttribute('aria-pressed', state.view === pair[0] ? 'true' : 'false');
        btn.textContent = pair[1];
        btn.addEventListener('click', function () { state.view = pair[0]; render(); });
        wrap.appendChild(btn);
      });
    });
    document.querySelectorAll('[data-cx-phone-stage]').forEach(function (el) {
      var artOpts = { title: state.title, message: state.msg, edition: state.edition, date: state.date, density: state.density, from: state.from, to: state.to };
      var html;
      if (v.isPoster) { artOpts.paper = state.garment; html = '<div class="cx-stage-inner cx-poster">' + posterCard(state.loc, state.style, artOpts) + '</div>'; }
      else { html = '<div class="cx-stage-inner">' + teeFragment(state.loc, state.style, state.garment, artOpts, v.teeVariant && v.teeVariant.image) + '</div>'; }
      el.innerHTML = html;
    });
    // Exactly one step panel is shown at a time, in normal document flow --
    // there's always an active one (never all collapsed), so the page never
    // shows a blank gap where the sheet used to be.
    document.querySelectorAll('[data-cx-sheet]').forEach(function (sheet) {
      sheet.querySelectorAll('[data-sheet-panel]').forEach(function (p) { p.classList.toggle('cx-active', p.getAttribute('data-sheet-panel') === state.tab); });
    });
    document.querySelectorAll('[data-cx-phone-tabbar]').forEach(function (wrap) {
      wrap.innerHTML = '';
      TABS.forEach(function (t) {
        var btn = document.createElement('button');
        btn.type = 'button'; btn.className = 'cx-tab';
        btn.setAttribute('aria-pressed', state.tab === t[0] ? 'true' : 'false');
        btn.textContent = t[1];
        btn.addEventListener('click', function () { state.tab = t[0]; render(); });
        wrap.appendChild(btn);
      });
    });
    document.querySelectorAll('[data-cx-phone-atc]').forEach(function (e) {
      e.textContent = '€' + (v.price != null ? v.price : '—');
      e.disabled = !v.variant || !v.variant.available;
    });
  }

  /* ── real Shopify cart line-item properties ──────────────────── */
  function syncProperties(v) {
    var isFeaturedDefault = state.loc !== 'custom' && state.radiusKm === ((LOC_META[state.loc] || {}).halfHKm || RADIUS_DEFAULT_KM);
    var map = {
      'cx-prop-location': v.loc.label,
      'cx-prop-coordinates': fmtCoords(v.loc.center, false),
      'cx-prop-style': v.st.name + (state.style === 'custom' ? ' ' + state.from.toUpperCase() + ' → ' + state.to.toUpperCase() : '') + ', ' + state.density + ' lines',
      'cx-prop-title': state.title,
      'cx-prop-message': state.msg,
      'cx-prop-edition': state.edition,
      'cx-prop-date': state.date,
      'cx-prop-product': v.isPoster ? 'Poster ' + state.posterSize + ', ' + posterDim(state.posterSize) : 'Tee ' + state.size,
      'cx-prop-colour': v.colourName + ' / ' + state.garment,
      'cx-prop-sku': v.sku,
      'cx-prop-coverage': state.radiusKm + ' km radius',
      'cx-prop-source': isFeaturedDefault ? 'Real elevation, featured trace' : 'Real elevation, traced live at checkout'
    };
    Object.keys(map).forEach(function (id) { var el = document.getElementById(id); if (el) el.value = map[id]; });
    var idInput = document.getElementById('cx-input-id');
    if (idInput && v.variant) idInput.value = v.variant.id;
  }

  /* ── wiring ──────────────────── */
  function bindText(selector, key, maxLen) {
    document.querySelectorAll(selector).forEach(function (el) {
      el.value = state[key];
      if (maxLen) el.maxLength = maxLen;
      el.addEventListener('input', function (e) { state[key] = e.target.value; render(); });
    });
  }

  function wire() {
    bindText('[data-cx-title]', 'title', 32);
    bindText('[data-cx-msg]', 'msg', 55);
    bindText('[data-cx-edition]', 'edition', 22);
    bindText('[data-cx-date]', 'date', 18);
    document.querySelectorAll('[data-cx-query]').forEach(function (el) {
      el.addEventListener('input', function (e) {
        state.query = e.target.value;
        render();
        scheduleGeocode(e.target.value);
      });
    });
    document.querySelectorAll('input[type="range"][data-cx-density]').forEach(function (el) {
      el.addEventListener('input', function (e) { state.density = Number(e.target.value); render(); });
    });
    document.querySelectorAll('input[type="range"][data-cx-radius]').forEach(function (el) {
      el.addEventListener('input', function (e) {
        state.radiusKm = Number(e.target.value);
        render();
        var loc = DATA[state.loc];
        if (loc) scheduleRetrace(state.loc, loc.center[0], loc.center[1], state.radiusKm);
      });
    });
    document.querySelectorAll('[data-cx-view-toggle]').forEach(function (wrap) {
      wrap.querySelectorAll('button').forEach(function (btn, i) {
        btn.addEventListener('click', function () { state.view = i === 0 ? 'poster' : 'shirt'; render(); });
      });
    });
    document.querySelectorAll('[data-cx-back]').forEach(function (b) { b.addEventListener('click', function () { state.step = Math.max(1, state.step - 1); render(); }); });
    document.querySelectorAll('[data-cx-continue]').forEach(function (b) { b.addEventListener('click', function () { state.step = Math.min(4, state.step + 1); render(); }); });
    document.querySelectorAll('[data-cx-step-edit]').forEach(function (b) {
      b.addEventListener('click', function () { state.step = Number(b.getAttribute('data-cx-step-edit')); render(); });
    });
    // Step headers are clickable at any time, so the wizard doesn't force
    // going through steps in order.
    document.querySelectorAll('.cx-step-head-left').forEach(function (el) {
      var stepEl = el.closest('[data-cx-step]');
      var n = stepEl ? Number(stepEl.getAttribute('data-cx-step')) : null;
      if (n) el.addEventListener('click', function () { state.step = n; render(); });
    });

    var form = document.getElementById('cx-product-form');
    if (form) {
      form.addEventListener('submit', function () {
        var v = computeVals();
        var idInput = document.getElementById('cx-input-id');
        if (idInput && v.variant) idInput.value = v.variant.id;
      });
    }
    document.querySelectorAll('[data-cx-atc], [data-cx-phone-atc]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var f = document.getElementById('cx-product-form');
        if (f) f.requestSubmit ? f.requestSubmit() : f.submit();
      });
    });

    wireStageZoom();
    wireAdmin();
  }

  // Hover-zoom (desktop) / tap-to-zoom (phone) on the preview stage: scales
  // the stage's content around the pointer via transform-origin, so
  // customers can see fine contour/text detail without a separate zoomed
  // image. Neither [data-cx-stage] nor [data-cx-phone-stage] is ever
  // replaced by render() (only their innerHTML is), so this only needs to
  // be wired once, and always re-looks-up the current .cx-stage-inner
  // rather than caching it.
  function stageZoomOrigin(stage, clientX, clientY) {
    var rect = stage.getBoundingClientRect();
    var x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    var y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    return x + '% ' + y + '%';
  }

  function wireStageZoom() {
    document.querySelectorAll('[data-cx-stage]').forEach(function (stage) {
      function setOrigin(e) {
        var inner = stage.querySelector('.cx-stage-inner');
        if (inner) inner.style.transformOrigin = stageZoomOrigin(stage, e.clientX, e.clientY);
      }
      stage.addEventListener('mouseenter', function (e) { stage.classList.add('is-zoomed'); setOrigin(e); });
      stage.addEventListener('mousemove', setOrigin);
      stage.addEventListener('mouseleave', function () {
        stage.classList.remove('is-zoomed');
        var inner = stage.querySelector('.cx-stage-inner');
        if (inner) inner.style.transformOrigin = '50% 50%';
      });
    });

    // No hover on touch, so the equivalent gesture is tap-to-toggle: the
    // first tap zooms in centered on where the customer tapped, the next
    // tap anywhere on the stage zooms back out.
    document.querySelectorAll('[data-cx-phone-stage]').forEach(function (stage) {
      stage.addEventListener('click', function (e) {
        var inner = stage.querySelector('.cx-stage-inner');
        if (!inner) return;
        if (stage.classList.contains('is-zoomed')) {
          stage.classList.remove('is-zoomed');
          inner.style.transformOrigin = '50% 50%';
        } else {
          inner.style.transformOrigin = stageZoomOrigin(stage, e.clientX, e.clientY);
          stage.classList.add('is-zoomed');
        }
      });
    });
  }

  /* ── admin: high-res PNG export ────────────────────
     Draws straight to a <canvas> with the 2D drawing API (fillText, Path2D
     for the contour lines, strokeRect for frames) instead of the previous
     approach of rasterizing an <svg><foreignObject> of the HTML fragment.
     That approach hit a hard, unfixable browser rule: a canvas that has
     ever drawn an SVG image containing a foreignObject is permanently
     "tainted" and refuses to export (toBlob/toDataURL throw) -- true
     regardless of fonts, CORS, or what's actually inside the foreignObject.
     Reuses the exact same contourLines()/deriveTheme() output the on-screen
     preview uses (via Path2D on the raw SVG path strings), so the export
     matches what's shown, just isn't going through SVG-as-image at all. */
  function downloadBlob(blob, filename) {
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
  }

  // Greedy word-wrap against the current ctx.font, mirroring text-wrap:balance
  // closely enough for a title/message that's usually 1-2 lines.
  function wrapCanvasText(ctx, text, maxWidth) {
    if (!text) return [];
    var words = text.split(/\s+/).filter(Boolean);
    if (!words.length) return [];
    var lines = [], cur = words[0];
    for (var i = 1; i < words.length; i++) {
      var test = cur + ' ' + words[i];
      if (ctx.measureText(test).width > maxWidth) { lines.push(cur); cur = words[i]; }
      else { cur = test; }
    }
    lines.push(cur);
    return lines;
  }

  // Draws the same {d, c, w} segments svgLines() turns into SVG <path>
  // elements, but as canvas Path2D strokes, mapped from their [0,100]
  // (or, for rainbow's crop, [vbOrigin, vbOrigin+vbSize]) coordinate
  // space into rectX/Y/W/H -- 'meet' letterboxes (fit), 'slice' crops
  // to fill, matching the two preserveAspectRatio modes the preview uses.
  function drawContourArt(ctx, lines, rectX, rectY, rectW, rectH, vbOrigin, vbSize, mode) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(rectX, rectY, rectW, rectH);
    ctx.clip();
    var scale = mode === 'slice' ? Math.max(rectW / vbSize, rectH / vbSize) : Math.min(rectW / vbSize, rectH / vbSize);
    var offX = rectX + (rectW - vbSize * scale) / 2 - vbOrigin * scale;
    var offY = rectY + (rectH - vbSize * scale) / 2 - vbOrigin * scale;
    ctx.translate(offX, offY);
    ctx.scale(scale, scale);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    lines.forEach(function (l) {
      ctx.strokeStyle = l.c;
      ctx.lineWidth = l.w;
      ctx.stroke(new Path2D(l.d));
    });
    ctx.restore();
  }

  function drawRoundRectStroke(ctx, x, y, w, h, r, color, lineWidth) {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    ctx.roundRect(x + lineWidth / 2, y + lineWidth / 2, w - lineWidth, h - lineWidth, r);
    ctx.stroke();
    ctx.restore();
  }

  // Mirrors posterFragment()'s non-thumb output, cqw-for-cqw (1cqw == 1% of
  // canvas width W, exactly like the on-screen version where the poster's
  // own width is the container-query axis).
  function renderPosterCanvas(ctx, W, H, locId, style, opts) {
    opts = opts || {};
    var loc = DATA[locId];
    var meta = LOC_META[locId] || { short: '' };
    // bare: true (the admin PNG export always sets this) skips the
    // full-bleed background fill, since the print file itself should be
    // transparent -- paperHex still gets derived, from paperForTheme,
    // since deriveTheme() needs *some* colour to compute contrast-correct
    // ink/frame/rule against.
    var paperHex = opts.bare ? (opts.paperForTheme || '#171012') : (opts.paper || (style === 'rainbow' ? '#f0ede8' : '#171012'));
    var themeInfo = deriveTheme(style, paperHex, opts.from, opts.to);
    var dens = Math.max(20, Math.min(50, Math.round(opts.density || 22)));
    var lines = contourLines(locId, dens, false, style, themeInfo);
    var title = String(opts.title || loc.title || '').toUpperCase();
    var message = String(opts.message || '');
    var edition = String(opts.edition || '').toUpperCase();
    var date = String(opts.date || '').toUpperCase();
    var coords = fmtCoords(loc.center, true);
    var t = themeInfo.theme;
    function cq(v) { return v / 100 * W; }

    if (!opts.bare) {
      ctx.fillStyle = paperHex;
      ctx.fillRect(0, 0, W, H);
    }
    ctx.textBaseline = 'alphabetic';

    if (style !== 'rainbow') {
      var outerPad = cq(4);
      var borderW = cq(0.3);
      drawRoundRectStroke(ctx, outerPad, outerPad, W - 2 * outerPad, H - 2 * outerPad, 0, t.frame, borderW);

      var contentX = outerPad + cq(4.6);
      var contentW = W - 2 * outerPad - 2 * cq(4.6);
      var contentTop = outerPad + cq(5);
      var contentBottom = H - outerPad - cq(4);
      var gap = cq(2.6);

      var titleSize = cq(8.6);
      ctx.font = '400 ' + titleSize + 'px "Bebas Neue"';
      var titleLines = wrapCanvasText(ctx, title, contentW);
      var titleLH = titleSize * 0.88;

      var msgSize = cq(2.9);
      ctx.font = '300 ' + msgSize + 'px "Inter"';
      var msgLines = wrapCanvasText(ctx, message, contentW);
      var msgLH = msgSize * 1.55;

      var metaSize = cq(1.9);
      var metaLH = metaSize * 1.3;

      var coordsSize = cq(3.1);
      var coordsBorderTop = cq(0.15);
      var coordsPadTop = cq(2.4);
      var coordsLH = coordsSize * 1.15;

      var fixedH = titleLines.length * titleLH + msgLines.length * msgLH + metaLH +
        (coordsBorderTop + coordsPadTop + coordsLH) + gap * 4;
      var artH = Math.max(cq(12), contentBottom - contentTop - fixedH);

      var y = contentTop;
      ctx.fillStyle = t.ink;
      ctx.textAlign = 'left';
      ctx.font = '400 ' + titleSize + 'px "Bebas Neue"';
      titleLines.forEach(function (ln, i) { ctx.fillText(ln, contentX, y + titleLH * (i + 0.86)); });
      y += titleLines.length * titleLH + gap;

      drawContourArt(ctx, lines, contentX, y, contentW, artH, 0, 100, 'meet');
      y += artH + gap;

      ctx.fillStyle = t.body;
      ctx.font = '300 ' + msgSize + 'px "Inter"';
      msgLines.forEach(function (ln, i) { ctx.fillText(ln, contentX, y + msgLH * (i + 0.78)); });
      y += msgLines.length * msgLH + gap;

      ctx.font = '400 ' + metaSize + 'px "JetBrains Mono"';
      ctx.fillStyle = t.meta;
      var metaBaseline = y + metaLH * 0.8;
      ctx.textAlign = 'left'; ctx.fillText(edition, contentX, metaBaseline);
      ctx.textAlign = 'center'; ctx.fillText(date, contentX + contentW / 2, metaBaseline);
      ctx.textAlign = 'right'; ctx.fillText((meta.short || '').toUpperCase(), contentX + contentW, metaBaseline);
      y += metaLH + gap;

      ctx.strokeStyle = t.rule;
      ctx.lineWidth = coordsBorderTop;
      ctx.beginPath();
      ctx.moveTo(contentX, y + coordsBorderTop / 2);
      ctx.lineTo(contentX + contentW, y + coordsBorderTop / 2);
      ctx.stroke();
      y += coordsBorderTop + coordsPadTop;
      ctx.font = '400 ' + coordsSize + 'px "JetBrains Mono"';
      ctx.fillStyle = t.ink;
      ctx.textAlign = 'center';
      ctx.fillText(coords, contentX + contentW / 2, y + coordsLH * 0.8);

    } else {
      var padTop = cq(5), padSide = cq(5), padBottom = cq(4);
      var cx2 = padSide, cw2 = W - 2 * padSide;
      var gap2 = cq(3);

      var titleSize2 = cq(10.5);
      ctx.font = '400 ' + titleSize2 + 'px "Bebas Neue"';
      var titleLines2 = wrapCanvasText(ctx, title, cw2);
      var titleLH2 = titleSize2 * 0.84;

      var msgSize2 = cq(2.9);
      ctx.font = '400 ' + msgSize2 + 'px "Inter"';
      var msgLines2 = wrapCanvasText(ctx, message, cw2);
      var msgLH2 = msgSize2 * 1.5;

      var metaSize2 = cq(1.9);
      var metaLH2 = metaSize2 * 1.3;

      var coordsSize2 = cq(4.4);
      var coordsLH2 = coordsSize2 * 1.05;

      var fixedH2 = titleLines2.length * titleLH2 + msgLines2.length * msgLH2 + metaLH2 + coordsLH2 + gap2 * 4;
      var contentTop2 = padTop, contentBottom2 = H - padBottom;
      var artH2 = Math.max(cq(12), contentBottom2 - contentTop2 - fixedH2);

      var y2 = contentTop2;
      ctx.fillStyle = t.ink;
      ctx.textAlign = 'left';
      ctx.font = '400 ' + titleSize2 + 'px "Bebas Neue"';
      titleLines2.forEach(function (ln, i) { ctx.fillText(ln, cx2, y2 + titleLH2 * (i + 0.84)); });
      y2 += titleLines2.length * titleLH2 + gap2;

      var frameW = cq(1.5), frameR = cq(5);
      drawRoundRectStroke(ctx, cx2, y2, cw2, artH2, frameR, t.frame, frameW);
      drawContourArt(ctx, lines, cx2 + frameW, y2 + frameW, cw2 - 2 * frameW, artH2 - 2 * frameW, 6, 88, 'slice');
      y2 += artH2 + gap2;

      ctx.fillStyle = t.body;
      ctx.font = '400 ' + msgSize2 + 'px "Inter"';
      msgLines2.forEach(function (ln, i) { ctx.fillText(ln, cx2, y2 + msgLH2 * (i + 0.75)); });
      y2 += msgLines2.length * msgLH2 + gap2;

      ctx.font = '400 ' + metaSize2 + 'px "JetBrains Mono"';
      ctx.fillStyle = t.meta;
      var metaBaseline2 = y2 + metaLH2 * 0.8;
      ctx.textAlign = 'left'; ctx.fillText(edition, cx2, metaBaseline2);
      ctx.textAlign = 'center'; ctx.fillText(date, cx2 + cw2 / 2, metaBaseline2);
      ctx.textAlign = 'right'; ctx.fillText((meta.short || '').toUpperCase(), cx2 + cw2, metaBaseline2);
      y2 += metaLH2 + gap2;

      ctx.font = '400 ' + coordsSize2 + 'px "JetBrains Mono"';
      ctx.fillStyle = t.ink;
      ctx.textAlign = 'center';
      ctx.fillText(coords, cx2 + cw2 / 2, y2 + coordsLH2 * 0.8);
    }
  }

  function exportPNG(kind) {
    var isPoster = kind === 'poster';
    // Both formats print the identical 3:4 artwork block (title, contour
    // art, message, meta row, coordinates) -- the physical background
    // (paper stock or garment fabric) is supplied by whatever the piece is
    // actually printed on, not baked into the file. So this always renders
    // just the print film itself, transparent, independent of the on-screen
    // mockup (no poster page, no garment photo/silhouette) -- paperForTheme
    // still feeds deriveTheme() so ink/frame colours stay contrast-correct
    // for whichever paper/garment colour is currently selected, it's only
    // the background *fill* that's skipped.
    var artOpts = {
      title: state.title, message: state.msg, edition: state.edition, date: state.date,
      density: state.density, from: state.from, to: state.to,
      bare: true, paperForTheme: state.garment
    };
    var W = 3600, H = 4800;
    var canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    var ctx = canvas.getContext('2d');
    var msg = document.getElementById('cx-admin-status');
    if (msg) msg.textContent = 'Rendering ' + W + '×' + H + 'px…';

    // document.fonts.ready only guarantees @font-face rules already in use
    // on the page have finished loading -- it does not guarantee the exact
    // family+weight combos the canvas is about to request have been
    // rasterized, so fillText can silently fall back to a system serif on
    // the first draw. Force-load each combo explicitly before rendering.
    var fontSpecs = ['400 100px "Bebas Neue"', '300 100px "Inter"', '400 100px "JetBrains Mono"'];
    var fontLoads = document.fonts
      ? fontSpecs.map(function (f) { try { return document.fonts.load(f); } catch (e) { return Promise.resolve(); } })
      : [];

    Promise.all(fontLoads)
      .then(function () { return document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve(); })
      .then(function () {
        renderPosterCanvas(ctx, W, H, state.loc, state.style, artOpts);
      })
      .then(function () {
        return new Promise(function (resolve, reject) {
          canvas.toBlob(function (b) { b ? resolve(b) : reject(new Error('canvas.toBlob returned null')); }, 'image/png');
        });
      })
      .then(function (blob) {
        downloadBlob(blob, 'contourist-' + state.loc + '-' + (isPoster ? 'poster' : 'shirt-print') + '.png');
        if (msg) msg.textContent = 'Downloaded ' + W + '×' + H + 'px print-ready art, transparent background. Placeholder pixel size, not confirmed Printful specs. Won’t save inside a Claude preview sandbox.';
      })
      .catch(function (err) { if (msg) msg.textContent = 'Export failed: ' + err.message; });
  }
  function wireAdmin() {
    if (!ADMIN) return;
    var panel = document.getElementById('cx-admin-panel');
    if (panel) panel.style.display = 'flex';
    var posterBtn = document.getElementById('cx-admin-export-poster');
    var shirtBtn = document.getElementById('cx-admin-export-shirt');
    if (posterBtn) posterBtn.addEventListener('click', function () { exportPNG('poster'); });
    if (shirtBtn) shirtBtn.addEventListener('click', function () { exportPNG('shirt'); });
  }

  function applyPrefillFromQuery() {
    var q = new URLSearchParams(window.location.search);
    ['loc', 'style', 'title', 'msg', 'edition', 'date', 'view', 'size', 'posterSize'].forEach(function (k) {
      var v = q.get(k);
      if (v) state[k] = v;
    });
    // A ?loc= override without an explicit ?radius= should still use that
    // location's own curated default, not whatever the boot sequence had
    // set for the original first-listed location.
    var radius = q.get('radius');
    state.radiusKm = radius ? Number(radius) : ((LOC_META[state.loc] || {}).halfHKm || RADIUS_DEFAULT_KM);
    var lat = q.get('lat'), lon = q.get('lon');
    if (lat && lon) {
      // Regenerating a custom (searched) order for the admin PNG export --
      // AWS/MapTiler tiles are a static dataset, so the same lat/lon/radius
      // always retraces identically.
      retrace('custom', Number(lat), Number(lon), state.radiusKm, 'search', { label: q.get('label') || 'Custom location', country: '' });
    } else if (DATA[state.loc] && radius) {
      // Regenerating a featured order at a non-default coverage radius.
      var c = DATA[state.loc].center;
      retrace(state.loc, c[0], c[1], state.radiusKm, 'radius');
    }
  }

  /* ── shared preview API ────────────────────
     The home page's Contourist feature section shows the same tee and
     poster the configurator does. It renders them through here rather than
     reimplementing the artwork, so both pages share one contour dataset,
     one deriveTheme() ink rule and one set of fragments. loadData() is
     memoised, so the topo JSON is fetched at most once per page. */
  var topoPromise = null;
  function loadData(url) {
    if (!topoPromise) {
      topoPromise = fetch(url)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (json) { DATA = json; return DATA; });
    }
    return topoPromise;
  }

  window.CxPreview = {
    load: loadData,
    posterCard: posterCard,
    teeFragment: teeFragment,
    fmtCoords: fmtCoords,
    meta: LOC_META
  };

  // ?focus=search deep-links the home page's "+ Your own location" chip
  // straight into the location search rather than just the right step.
  // Whichever of the desktop/phone inputs is the visible one gets focus.
  function focusSearchIfRequested() {
    if (new URLSearchParams(window.location.search).get('focus') !== 'search') return;
    var inputs = document.querySelectorAll('[data-cx-query]');
    for (var i = 0; i < inputs.length; i++) {
      if (inputs[i].offsetParent !== null) {
        inputs[i].scrollIntoView({ block: 'center' });
        inputs[i].focus();
        return;
      }
    }
  }

  /* ── boot ──────────────────── */
  function showLoadError(err) {
    document.querySelectorAll('[data-cx-stage]').forEach(function (el) {
      el.innerHTML = '<div class="cx-skeleton">Could not load location data.<br>' + esc(err.message) + '</div>';
    });
  }

  if (root) {
    loadData(TOPO_URL)
      .then(function () {
        state.loc = Object.keys(DATA)[0];
        state.radiusKm = (LOC_META[state.loc] || {}).halfHKm || RADIUS_DEFAULT_KM;
        applyPrefillFromQuery();
        wire();
        render();
        focusSearchIfRequested();
      })
      .catch(showLoadError);
  }
})();
